import { Capacitor, registerPlugin } from "@capacitor/core";
import type { OrganizationDetails } from "~vue/appTypes";

import { logError } from "./logError";

interface SharingPolicyPlugin {
    setPolicy(options: { restricted: boolean }): Promise<void>;
    shareFile(options: { url: string }): Promise<{ completed: boolean }>;
}

const SharingPolicy = registerPlugin<SharingPolicyPlugin>("SharingPolicy");

export const SHARING_RESTRICTED_CLASS = "native-sharing-restricted";
// Without native menu enforcement, the only way to keep selected text out of the system Share menu is to prevent selection.
export const SELECTION_BLOCKED_CLASS = "native-sharing-selection-blocked";

export function isMobileSharingRestricted(organizationDetails: OrganizationDetails | null | undefined): boolean {
    return Capacitor.isNativePlatform() && organizationDetails?.is_mobile_sharing_restricted === true;
}

// Older binaries ship without the plugin; restricted orgs must then avoid the stock share sheet entirely.
export function canShareRestrictedFiles(): boolean {
    return Capacitor.isPluginAvailable("SharingPolicy");
}

async function enforceNatively(restricted: boolean): Promise<boolean> {
    if (!canShareRestrictedFiles()) {
        return false;
    }
    try {
        await SharingPolicy.setPolicy({ restricted });
        return true;
    } catch (error) {
        logError(error, { tags: { source: "native_sharing_policy" }, context: { sharing_policy: { restricted } } });
        return false;
    }
}

export async function applyNativeSharingPolicy(organizationDetails: OrganizationDetails | null | undefined): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
        return;
    }
    const restricted = isMobileSharingRestricted(organizationDetails);
    const root = document.documentElement;
    root.classList.toggle(SHARING_RESTRICTED_CLASS, restricted);
    if (restricted) {
        root.classList.add(SELECTION_BLOCKED_CLASS);
    }
    const enforced = await enforceNatively(restricted);
    root.classList.toggle(SELECTION_BLOCKED_CLASS, restricted && !enforced);
}

// Shares a cached file without AirDrop, Print or Copy. The native side deletes the file afterwards.
export async function shareRestrictedFile(url: string): Promise<void> {
    await SharingPolicy.shareFile({ url });
}
